@if (isVisible$ | async) {
  <header
    class="sticky-nav white-bg surface-primary medium-type fill"
    [class.auth-overlay-open]="authOverlayOpen$ | async"
    data-elastic-exclude
  >
    <!-- Top nav desktop start here -->
    <div
      class="navigation-heading-top center grid-max-width padding-left-gutter padding-right-gutter flex dn-laptop-down flex--space-between flex--middle ps-xsmall-y font-body-1 font-body-medium"
      data-testid="header-heading"
    >
      <div class="flex flex--middle">
        <a
          [finLink]="homeLink$ | async"
          data-testid="finnair-logo-link"
          (click)="headerGtmClickEvent('finnair-logo', GaContext.TOP_NAVIGATION)"
        >
          <span class="is-vishidden">{{ 'actions.home' | finLocalization }}</span>
          <fcom-icon
            class="finnair-blue nordic-blue-900-text text-icon-primary"
            [category]="IconLibrary.CUSTOM"
            [name]="CustomIcon.FINNAIR_LOGO"
          />
        </a>

        <a
          [finLink]="oneworldLink$ | finMap: 'url' | async"
          (click)="headerGtmClickEvent('oneworld-logo', GaContext.TOP_NAVIGATION)"
          data-testid="oneworld-link"
          class="ms-large-l font-body-1 font-body-medium"
        >
          <span class="is-vishidden">{{ oneworldLink$ | finMap: 'title' | async }}</span>
          <fcom-icon class="icon-medium" [name]="SvgLibraryIcon.ONEWORLD_LOGO" />
        </a>
      </div>

      <div class="flex flex--middle">
        <!-- Language selector -->
        <div class="align-right">
          <fin-language-selector [context]="LANGUAGE_SELECTOR_CONTEXT" />
        </div>

        <!-- Login -->
        <div class="login-item ms-large-l align-right font-body-1 mr-xsmall-l font-body-medium">
          @if (loginStatus$ | async; as loginStatus) {
            <fin-login-button
              [showName]="true"
              [trackingContext]="GaContext.TOP_NAVIGATION"
              class="font-body-1 grow flex nordic-blue-900-text text-icon-primary font-body-medium"
              (click)="handleLoginButtonClick($event, loginStatus)"
            />
            @if ((loginDialogOpen$ | async) && (mobileOrTablet$ | async) === false) {
              <fin-login-form
                class="absolute ms-xxsmall-t dn-laptop-down"
                [accountVerificationToken$]="accountVerificationToken$"
                [verificationChannel$]="verificationChannel$"
                [verificationSuccessUrl$]="verificationSuccessUrl$"
                (verificationFinished)="accountVerificationFinished()"
              />
            }
          }
        </div>

        @if (enableLoginDialog && (loginStatus$ | async) !== LoginStatus.LOGGED_IN) {
          <fcom-button
            data-testid="join-finnair-plus-button"
            (clickBtn)="joinFinnairPlus()"
            [class]="'mr-xsmall-l'"
            [text]="'login.joinFinnairPlus' | finLocalization"
            [icon]="{ name: SvgLibraryIcon.FINNAIR_PLUS, position: IconPosition.LEFT }"
            [mode]="ButtonMode.INLINE"
            [theme]="ButtonTheme.LIGHT_TERTIARY_GHOST"
          />
        }
        @if ((joinService.joinDialogOpen$ | async) && (mobileOrTablet$ | async) === false) {
          <div class="relative dn-laptop-down">
            <fin-join-form class="absolute ms-large-t" />
          </div>
        }
        @if (welcomeBackMessageOpen$ | async) {
          <fin-welcome-back-message />
        }
      </div>
    </div>
    @if ((loginDialogOpen$ | async) && (mobileOrTablet$ | async)) {
      <fin-login-form
        class="dn-laptop-up"
        [fullScreen]="true"
        [accountVerificationToken$]="accountVerificationToken$"
        [verificationChannel$]="verificationChannel$"
        [verificationSuccessUrl$]="verificationSuccessUrl$"
        (verificationFinished)="accountVerificationFinished()"
      />
    }
    @if ((joinService.joinDialogOpen$ | async) && (mobileOrTablet$ | async)) {
      <fin-join-form class="dn-laptop-up" [fullScreen]="true" />
    }
    <fin-login-widget (isOpenChange)="loginWidgetOpen$.next($event)" />

    <!--Top nav mobile start here  -->
    <div
      class="grid-col-12 no-row-gap grid-max-width border-top-dark-ice dn-laptop-up mobile-header-controls border-bottom"
    >
      <fin-header-boxes
        class="col-f"
        [homeLink$]="homeLink$"
        (homeGtmEvent)="headerGtmClickEvent('finnair-logo', GaContext.TOP_NAVIGATION)"
      >
        <div left class="flex flex--center">
          <fin-navigation-trigger
            (toggleMenuGtmEvent)="
              headerGtmClickEvent('mobile-main-navigation-hamburger-navi', GaContext.MOBILE_MAIN_NAVIGATION)
            "
            class="flex--nogrow inline-block relative"
          />
        </div>
        <div right>
          @if ((isMenuOpen$ | async) !== true && loginStatus$ | async; as loginStatus) {
            <fin-login-button
              class="grow flex nordic-blue-900-text text-icon-primary"
              (click)="openLoginWidget($event)"
              [showName]="false"
              [trackingContext]="GaContext.TOP_NAVIGATION"
            />
          }
        </div>
      </fin-header-boxes>
      <div class="col-f"></div>
    </div>

    <!-- Lower nav start here -->
    <div class="relative z2" (finClickOutside)="clickOutside()" data-testid="navigation-header">
      <div class="nordic-blue-900-bg surface-contrast" data-testid="navigation-heading">
        <div
          class="center grid-max-width padding-left-gutter padding-right-gutter flex flex--space-between flex--middle navigation-heading-bottom"
          [class.extended-navi-padding]="!!(amountOfUpcomingTrips$ | async)"
          data-testid="navigation-heading-container"
        >
          <!-- Menu button set start here -->
          <div class="flex dn-laptop-down misc-menu">
            <fcom-button
              data-testid="navigation-toggle"
              [aria]="{
                controls: 'main-navigation',
                expanded: isOpen,
              }"
              [icon]="{
                name: isOpen ? SvgLibraryIcon.CLOSE_DELETE : SvgLibraryIcon.MENU_LIST,

                position: IconPosition.LEFT,
              }"
              [text]="'navigation.menu' | finLocalization"
              [theme]="ButtonTheme.DARK_TERTIARY_GHOST"
              (clickBtn)="toggleNavigation($event)"
            />
          </div>

          <!-- Menu category options start here -->
          <div class="desktop-header-wrapper nordic-blue-900-bg surface-contrast fill">
            <div class="flex flex--center grow align-center white-text text-icon-reversed">
              @for (item of topMenuItems$ | async; track item.trackingId) {
                <div class="menu-item flex text-hover pr-xsmall-x" [class.active]="item.active">
                  <a
                    class="no-underline white-text text-icon-reversed inline-block fill font-body-1 font-body-medium"
                    [attr.aria-current]="item.active ? 'page' : null"
                    [finLink]="item.link"
                    [external]="item.external"
                    [attr.target]="item.openInNewTab ? '_blank' : ''"
                    [finElemTracker]="'top-header: ' + item.trackingId"
                    [finElemContext]="GaContext.TOP_NAVIGATION"
                    [finElemType]="ElementTypes.LINK"
                    data-testid="header-menu-item-link"
                    (click)="scrollToTop()"
                  >
                    @if (item.link.includes('/manage') && (amountOfUpcomingTrips$ | async)) {
                      {{ 'navigation.myTrips' | finLocalization: { amount: (amountOfUpcomingTrips$ | async) } }}
                    } @else {
                      {{ item.teaserTitle }}
                    }
                    @if (item.active) {
                      <span class="is-vishidden">{{ 'navigation.currentPage' | finLocalization }}</span>
                    }
                  </a>
                </div>
              }
            </div>
          </div>

          <!-- Search set start here -->
          <div class="misc-search dn-laptop-down">
            <div class="relative grow">
              <fin-site-search-header-widget-starter
                class="search-component block icon-button white-text text-icon-reversed grow"
                [expandable]="true"
                [context]="'desktop'"
                [context]="'header'"
                (searchGtmEvent)="
                  headerGtmClickEvent('desktop-main-navigation-search-navi', GaContext.DESKTOP_MAIN_NAVIGATION)
                "
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Main menu start here -->
      <nav
        id="main-navigation"
        class="border-bottom ps-small-t absolute z1 white-bg surface-primary fill"
        [attr.aria-label]="'navigation.mainNavigation' | finLocalization"
        [class.is-vishidden]="!isOpen"
      >
        <div class="grid-max-width padding-left-gutter padding-right-gutter relative desktop-navigation-wrapper center">
          <div class="desktop-navigation-head flex flex--space-between">
            @for (item of (menuItems$ | async)?.main; track $index; let catIdx = $index) {
              @if (item.teaserTitle && item.items && item.items.length > 0 && !item.hideInMenu) {
                <button
                  class="navigation-category nordic-blue-900-text text-icon-primary font-body-2 ps-xxsmall no-decoration font-body-small"
                  [attr.aria-controls]="'category-' + catIdx"
                  [attr.aria-expanded]="(expandedCategory$ | async) === item ? true : false"
                  [class.active]="(expandedOrActiveNaviCategory$ | async) === item"
                  [finElemTracker]="'level-1: ' + item.teaserTitle"
                  [finElemContext]="GaContext.TOP_NAVIGATION"
                  [finElemType]="ElementTypes.LINK"
                  (click)="setCategoryAsExpanded($event, item)"
                >
                  @if ((activeNaviCategory$ | async) === item) {
                    <span class="is-vishidden">{{ 'navigation.currentPageUnderTopic' | finLocalization }}</span>
                  }
                  {{ item.teaserTitle }}
                </button>
              }
              <div
                class="white-bg surface-primary desktop-navigation-body absolute z2"
                [attr.aria-labelledby]="'category-' + catIdx"
                [attr.aria-hidden]="(expandedCategory$ | async) === item ? false : true"
                [class.navi-expanded]="(expandedCategory$ | async) === item"
                (click)="$event.stopPropagation()"
              >
                <div
                  class="desktop-navigation-body-content center grid-max-width padding-left-gutter padding-right-gutter ps-xxlarge-t ps-xsmall-b font-body-2 regular-type font-body-small"
                  [class.nav-active-category]="(expandedCategory$ | async) === item"
                >
                  @for (section of item.items; track $index) {
                    @if (section.link && !section.hideInMenu) {
                      <dl class="ms-large-b pr-medium-r">
                        <dt class="ms-xsmall-b">
                          <a
                            [finLink]="section.link"
                            class="nordic-blue-900-text text-icon-primary bold-type no-decoration flex flex--middle"
                            (click)="closeNaviMenu()"
                            [class.current-item]="section.active"
                            [attr.aria-current]="section.active ? 'page' : null"
                            [external]="section.external"
                            [finElemTracker]="'level-2: ' + section.link"
                            [finElemContext]="GaContext.TOP_NAVIGATION"
                            [finElemType]="ElementTypes.LINK"
                            [attr.target]="section.openInNewTab ? '_blank' : ''"
                          >
                            {{ section.teaserTitle }}
                            @if (section.active) {
                              <span class="is-vishidden">{{ 'navigation.currentPage' | finLocalization }}</span>
                            }
                            @if (section.external) {
                              <fcom-icon [name]="SvgLibraryIcon.EXTERNAL_TAB" class="icon-small ms-xxsmall-l" />
                            }
                            @if (section.loginRequired) {
                              <fcom-icon [name]="SvgLibraryIcon.LOCK_OPEN" class="icon-small ms-xxsmall-l" />
                            }
                          </a>
                        </dt>
                        @for (child of section.items; track $index) {
                          @if (child.link && child.teaserTitle && !child.hideInMenu) {
                            <dd>
                              <a
                                [finLink]="child.link"
                                class="nordic-blue-900-text text-icon-primary no-decoration ps-xxsmall-t block flex flex--middle"
                                (click)="closeNaviMenu()"
                                [class.current-item]="child.active"
                                [attr.aria-current]="child.active ? 'page' : null"
                                [external]="child.external"
                                [finElemTracker]="'level-3: ' + child.link"
                                [finElemContext]="GaContext.TOP_NAVIGATION"
                                [finElemType]="ElementTypes.LINK"
                                [attr.target]="child.openInNewTab ? '_blank' : ''"
                              >
                                {{ child.teaserTitle }}
                                @if (child.active) {
                                  <span class="is-vishidden">{{ 'navigation.currentPage' | finLocalization }}</span>
                                }
                                @if (child.external) {
                                  <fcom-icon [name]="SvgLibraryIcon.EXTERNAL_TAB" class="icon-small ms-xxsmall-l" />
                                }
                                @if (child.loginRequired) {
                                  <fcom-icon [name]="SvgLibraryIcon.LOCK_OPEN" class="icon-small ms-xxsmall-l" />
                                }
                              </a>
                            </dd>
                          }
                        }
                      </dl>
                    }
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </nav>
    </div>
  </header>
}
@if (isVisible$ | async) {
  <fin-breadcrumb class="z1 fill" />
}
